import { useState, useCallback, useRef } from 'react';
import { Drawer, Spin } from 'antd';
import { Button } from '@bancobarigui/barihaus';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { InspectionRequest } from '@/services/types/inspection.types';
import { AmostrasData, AvaliacaoData } from '../types/laudo-wizard.types';
import { AmostrasStep } from './LaudoWizard/steps/AmostrasStep';

const DEFAULT_AMOSTRAS_DATA: AmostrasData = {
  amostras: [],
  metodologiaImportacao: 'manual',
  dadosAreaConstruida: [],
  dadosDepreciacao: [],
  dadosValorTerreno: [],
  dadosComparativoDireto: [],
  switchesTerreno: {
    area: false,
    testada: false,
    vegetacao: false,
    fatorExtraI: false,
    fatorExtraII: false,
    fatorExtraIII: false,
  },
};

interface PreliminaryCalculationsDrawerProps {
  open: boolean;
  inspection: InspectionRequest;
  initialData: AmostrasData | null;
  onSave: (data: AmostrasData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

export function PreliminaryCalculationsDrawer({
  open,
  inspection,
  initialData,
  onSave,
  onClose,
  saving,
}: PreliminaryCalculationsDrawerProps) {
  const [amostrasData, setAmostrasData] = useState<AmostrasData>(
    initialData || DEFAULT_AMOSTRAS_DATA
  );
  const dataRef = useRef(amostrasData);
  dataRef.current = amostrasData;

  const avaliacaoData: AvaliacaoData = {
    dadosImovel: {
      tipoImovel: 'Apartamento',
      tipoImplantacao: '',
      areaConstruida: 0,
      quantidadeQuartos: 0,
      quantidadeBanheiros: 0,
      quantidadeVagas: 0,
      idadeAparente: 0,
      estadoConservacao: '',
      padraoAcabamento: 'Normal',
    },
    dadosTerreno: {
      areaTerreno: 0,
      testada: 0,
      padraoTerreno: '',
      topografia: '',
    },
    dadosCondominio: {
      areaComum: 0,
      estadoConservacao: '',
    },
  };

  const handleUpdate = useCallback((data: Partial<AmostrasData>) => {
    setAmostrasData((prev) => {
      const updated = { ...prev, ...data };
      dataRef.current = updated;
      return updated;
    });
  }, []);

  const handleSave = useCallback(async () => {
    await onSave(dataRef.current);
  }, [onSave]);

  const handleAfterOpenChange = useCallback(
    (visible: boolean) => {
      if (visible) {
        setAmostrasData(initialData || DEFAULT_AMOSTRAS_DATA);
      }
    },
    [initialData]
  );

  return (
    <Drawer
      title="Memorial de Cálculo (Preliminar)"
      placement="right"
      width="100%"
      open={open}
      onClose={onClose}
      afterOpenChange={handleAfterOpenChange}
      destroyOnClose
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="default"
            icon={<CloseOutlined />}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Salvar
          </Button>
        </div>
      }
    >
      {saving && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip="Salvando..." />
        </div>
      )}
      <AmostrasStep
        data={amostrasData}
        onUpdate={handleUpdate}
        inspection={inspection}
        avaliacaoData={avaliacaoData}
        mode="create"
      />
    </Drawer>
  );
}
